import { instantFromIso } from "@nijiurachan/js/util/time";

export const ANNOUNCEMENT_READ_STORAGE_KEY =
  "nijiurachan-announce-read-article-published-at";
export const ANNOUNCEMENT_READ_REVISION_STORAGE_KEY =
  "nijiurachan-announce-read-article-unread-revision";

type AnnouncementReadStorage = Pick<Storage, "getItem" | "setItem">;

export function announcementTimestampEpoch(
  timestamp: string | null,
): number | null {
  if (timestamp === null) return null;
  return instantFromIso(timestamp)?.epochMilliseconds ?? null;
}

/**
 * 既読値は有効な記事公開日時だけを受け付け、前進時だけ差し替える。
 * meta側の時計が巻き戻った場合や不正値では既読を進めない。
 */
export function advanceAnnouncementReadTimestamp(
  current: string | null,
  candidate: string | null,
): string | null {
  const candidateEpoch = announcementTimestampEpoch(candidate);
  if (candidateEpoch === null) return current;
  const currentEpoch = announcementTimestampEpoch(current);
  if (currentEpoch !== null && candidateEpoch <= currentEpoch) return current;
  return candidate;
}

export function isUnreadAnnouncement(
  articleTimestamp: string | null,
  readTimestamp: string | null,
): boolean {
  const articleEpoch = announcementTimestampEpoch(articleTimestamp);
  if (articleEpoch === null) return false;
  const readEpoch = announcementTimestampEpoch(readTimestamp);
  return readEpoch === null || articleEpoch > readEpoch;
}

export function advanceAnnouncementReadRevision(
  current: number | null,
  candidate: number | null,
): number | null {
  if (!isAnnouncementRevision(candidate)) return current;
  return current !== null && candidate <= current ? current : candidate;
}

export function isUnreadAnnouncementRevision(
  articleUnreadRevision: number,
  readRevision: number | null,
): boolean {
  return (
    isAnnouncementRevision(articleUnreadRevision) &&
    articleUnreadRevision > (readRevision ?? 0)
  );
}

export function applyAnnouncementReadStorageEvent(
  current: string | null,
  event: Pick<StorageEvent, "key" | "newValue">,
  storage: AnnouncementReadStorage | null = browserStorage(),
): string | null {
  if (event.key !== ANNOUNCEMENT_READ_STORAGE_KEY) return current;
  const next = advanceAnnouncementReadTimestamp(current, event.newValue);
  if (next === current && current !== event.newValue && current !== null) {
    // 別タブの古い書込が後着した場合は、メモリ上の新しい値を永続値へ戻す。
    persistAnnouncementReadTimestamp(current, storage);
  }
  return next;
}

export function applyAnnouncementReadRevisionStorageEvent(
  current: number | null,
  event: Pick<StorageEvent, "key" | "newValue">,
  storage: AnnouncementReadStorage | null = browserStorage(),
): number | null {
  if (event.key !== ANNOUNCEMENT_READ_REVISION_STORAGE_KEY) return current;
  const eventRevision = parseAnnouncementRevision(event.newValue);
  const next = advanceAnnouncementReadRevision(current, eventRevision);
  if (next === current && current !== eventRevision && current !== null) {
    persistAnnouncementReadRevision(current, storage);
  }
  return next;
}

export function readAnnouncementReadTimestamp(
  storage: AnnouncementReadStorage | null = browserStorage(),
): string | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(ANNOUNCEMENT_READ_STORAGE_KEY);
    return announcementTimestampEpoch(value) === null ? null : value;
  } catch {
    return null;
  }
}

export function readAnnouncementReadRevision(
  storage: AnnouncementReadStorage | null = browserStorage(),
): number | null {
  if (!storage) return null;
  try {
    return parseAnnouncementRevision(
      storage.getItem(ANNOUNCEMENT_READ_REVISION_STORAGE_KEY),
    );
  } catch {
    return null;
  }
}

/**
 * 永続値が候補より進んでいる場合は書き戻さない。読取・書込例外は呼び出し元へ
 * 伝播させず、Reactのメモリ状態だけで安全に継続できるようにする。
 */
export function persistAnnouncementReadTimestamp(
  candidate: string,
  storage: AnnouncementReadStorage | null = browserStorage(),
): void {
  if (!storage || announcementTimestampEpoch(candidate) === null) return;
  try {
    const stored = storage.getItem(ANNOUNCEMENT_READ_STORAGE_KEY);
    const next = advanceAnnouncementReadTimestamp(stored, candidate);
    if (next !== candidate || stored === candidate) return;
    storage.setItem(ANNOUNCEMENT_READ_STORAGE_KEY, candidate);
  } catch {
    // Web Storageが利用できない環境ではメモリ状態だけを更新する。
  }
}

export function persistAnnouncementReadRevision(
  candidate: number,
  storage: AnnouncementReadStorage | null = browserStorage(),
): void {
  if (!storage || !isAnnouncementRevision(candidate)) return;
  try {
    const stored = parseAnnouncementRevision(
      storage.getItem(ANNOUNCEMENT_READ_REVISION_STORAGE_KEY),
    );
    const next = advanceAnnouncementReadRevision(stored, candidate);
    if (next !== candidate || stored === candidate) return;
    storage.setItem(ANNOUNCEMENT_READ_REVISION_STORAGE_KEY, String(candidate));
  } catch {
    // Web Storageが利用できない環境ではメモリ状態だけを更新する。
  }
}

function parseAnnouncementRevision(value: string | null): number | null {
  if (value === null || !/^\d+$/.test(value)) return null;
  const revision = Number(value);
  return isAnnouncementRevision(revision) ? revision : null;
}

function isAnnouncementRevision(value: number | null): value is number {
  return Number.isSafeInteger(value) && (value ?? -1) >= 0;
}

function browserStorage(): AnnouncementReadStorage | null {
  try {
    return typeof globalThis.localStorage === "undefined"
      ? null
      : globalThis.localStorage;
  } catch {
    return null;
  }
}
